import {FC, useCallback, useState} from 'react'
import {useIntl} from 'react-intl'

/**
 * Dismissible "what's new" banner shown above the bulk-upload cards.
 *
 * Bumping the STORAGE_KEY suffix (apb-whatsnew-YYYY-MM-dismissed) when shipping
 * a future round of bulk-upload improvements will re-show the banner without
 * colliding with the previous dismissal. The same key is mirrored in the
 * Dumyah backoffice template so admins who toggle between both surfaces see
 * a single coherent announcement.
 */
const STORAGE_KEY = 'apb-whatsnew-2026-05-dismissed'

const readDismissed = (): boolean => {
  // Tolerate SSR / locked-down storage by treating any failure as "show the banner".
  try {
    return typeof window !== 'undefined' && window.localStorage?.getItem(STORAGE_KEY) === '1'
  } catch {
    return false
  }
}

const BulkWhatsNewNotice: FC = () => {
  const {formatMessage} = useIntl()
  const [visible, setVisible] = useState<boolean>(() => !readDismissed())

  const handleDismiss = useCallback(() => {
    try {
      window.localStorage?.setItem(STORAGE_KEY, '1')
    } catch {
      /* localStorage may be blocked; the banner still hides for the rest of this session. */
    }
    setVisible(false)
  }, [])

  if (!visible) return null

  // Each translatable string is rendered with `dangerouslySetInnerHTML` so the
  // message catalog can include <b> / <i> / <code> highlights. The strings are
  // author-controlled (not user input), so this is safe; runtime data is never
  // injected here.
  const bulletKeys = ['b1', 'b2', 'b3', 'b4', 'b5', 'b6'] as const
  const html = (id: string): {__html: string} => ({__html: formatMessage({id})})

  return (
    <div className='apb-banner' role='status' aria-label={formatMessage({id: 'products.bulk_whatsnew.title'})}>
      <button
        type='button'
        onClick={handleDismiss}
        aria-label={formatMessage({id: 'products.bulk_whatsnew.dismiss'})}
        title={formatMessage({id: 'products.bulk_whatsnew.dismiss'})}
        className='apb-banner-close'
      >
        <i className='fas fa-times' aria-hidden='true' />
      </button>

      <div className='apb-banner-body'>
        <i className='fas fa-star apb-banner-star' aria-hidden='true' />
        <div className='flex-grow-1' style={{minWidth: 0}}>
          <p className='apb-banner-title'>{formatMessage({id: 'products.bulk_whatsnew.title'})}</p>

          {/* The two headline changes a supplier MUST see. */}
          <ul className='apb-banner-bullets'>
            <li className='apb-bullet' dangerouslySetInnerHTML={html('products.bulk_whatsnew.callout1')} />
            <li className='apb-bullet' dangerouslySetInnerHTML={html('products.bulk_whatsnew.callout2')} />
          </ul>

          <details className='apb-details'>
            <summary className='apb-details-summary'>
              {formatMessage({id: 'products.bulk_whatsnew.changes_title'})}
              <i className='fas fa-chevron-down' aria-hidden='true' />
            </summary>
            <ul className='apb-changes-list'>
              {bulletKeys.map((k) => (
                <li key={k} dangerouslySetInnerHTML={html(`products.bulk_whatsnew.${k}`)} />
              ))}
            </ul>
          </details>
        </div>
      </div>
    </div>
  )
}

export default BulkWhatsNewNotice
